import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar, ChevronDown, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * MonthPicker — Apple-inspired month selector.
 *
 * Props:
 *   value:   "" (empty = current month) or "YYYY-MM"
 *   onChange(next):  "" or "YYYY-MM"
 *   monthsBack:  how many months back to render in the grid (default 24)
 *   testId:  base data-testid (default "detail-month-picker")
 */
export function MonthPicker({ value, onChange, monthsBack = 24, testId = "detail-month-picker" }) {
  const [open, setOpen] = useState(false);
  const now = useMemo(() => new Date(), []);
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const activeKey = value || currentKey;

  // Focused year in the grid — defaults to selected year, drives paging
  const [year, setYear] = useState(() => Number(activeKey.slice(0, 4)));

  const label = (() => {
    const [y, m] = activeKey.split("-").map(Number);
    if (!y || !m) return "This month";
    const isCurrent = activeKey === currentKey && !value;
    const short = new Date(y, m - 1, 1).toLocaleString("en", { month: "short", year: "numeric" });
    return isCurrent ? `${short} (This month)` : short;
  })();

  // Boundary: don't allow picking months in the future
  const minYear = now.getFullYear() - Math.ceil(monthsBack / 12);
  const maxYear = now.getFullYear();

  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  const pick = (m1) => {
    const nextKey = `${year}-${String(m1).padStart(2, "0")}`;
    onChange(nextKey === currentKey ? "" : nextKey);
    setOpen(false);
  };

  const isFuture = (m1) => {
    if (year < now.getFullYear()) return false;
    if (year > now.getFullYear()) return true;
    return m1 > now.getMonth() + 1;
  };

  const clearToCurrent = () => {
    onChange("");
    setYear(now.getFullYear());
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={cn(
            "h-9 rounded-full border border-border bg-card pl-3 pr-3 flex items-center gap-2 text-sm font-medium transition-all",
            "hover:bg-secondary hover:border-primary/40",
            open && "border-primary/60 bg-secondary"
          )}
        >
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="min-w-[110px] text-left">{label}</span>
          <ChevronDown className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", open && "rotate-180")} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid={`${testId}-panel`}
        align="end"
        sideOffset={8}
        className="w-64 rounded-2xl p-0 border-border/70 bg-card/95 backdrop-blur-xl shadow-lg overflow-hidden"
      >
        {/* Year pager */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border/50 bg-secondary/30">
          <button
            type="button"
            data-testid={`${testId}-year-prev`}
            onClick={() => setYear((y) => Math.max(minYear, y - 1))}
            disabled={year <= minYear}
            className="h-7 w-7 rounded-full flex items-center justify-center hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Previous year"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div data-testid={`${testId}-year-label`} className="text-sm font-semibold tracking-tight">{year}</div>
          <button
            type="button"
            data-testid={`${testId}-year-next`}
            onClick={() => setYear((y) => Math.min(maxYear, y + 1))}
            disabled={year >= maxYear}
            className="h-7 w-7 rounded-full flex items-center justify-center hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Next year"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Month grid */}
        <div className="grid grid-cols-3 gap-1.5 p-3">
          {months.map((m, i) => {
            const m1 = i + 1;
            const key = `${year}-${String(m1).padStart(2, "0")}`;
            const isActive = key === activeKey;
            const isNow = key === currentKey;
            const disabled = isFuture(m1);
            return (
              <button
                key={m}
                type="button"
                disabled={disabled}
                onClick={() => pick(m1)}
                data-testid={`${testId}-${key}`}
                className={cn(
                  "h-9 rounded-xl text-xs font-semibold transition-all relative flex items-center justify-center gap-1",
                  disabled && "text-muted-foreground/40 cursor-not-allowed",
                  !disabled && !isActive && "hover:bg-secondary text-foreground",
                  isActive && "bg-primary text-primary-foreground shadow-sm",
                  !isActive && isNow && "ring-1 ring-primary/40 text-primary"
                )}
              >
                {m}
                {isActive && <Check className="h-3 w-3" />}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-border/50 flex items-center justify-between px-3 py-2 bg-secondary/20">
          <button
            type="button"
            data-testid={`${testId}-jump-current`}
            onClick={clearToCurrent}
            className="text-xs font-medium text-primary hover:underline"
          >
            Jump to this month
          </button>
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest">
            {value ? "Custom" : "Live"}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
